import * as vp from 'vopay';
import { ChangeEvent, JSX, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { CustomAppendNoResultErrorMessage } from 'vopay';
import { useClientData } from '@/providers/ClientData.provider';
import { SinglePayment, ItemProps, FinancialInstitutionsType } from './types';
import useFetchInteracEmail from '@/hooks/useApi/useFetchInteracEmail';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import useFetchClientAccountList from '@/hooks/useApi/useFetchClientAccountList';
import usePayPal from '@/hooks/useApi/usePayPal';
import useVenmo from '@/hooks/useApi/useVenmo';
import SinglePaymentBankInfoContext from './SinglePaymentBankInfoContext';

import InteracInfoContext from '@/components/Accounts/AccountCreation/Interac/InteracInfoContext';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import styles from './SinglePaymentForm.module.scss';
import { useDataFetchIndicator } from '@/hooks/';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

type SelectOption = vp.SelectOption<vp.ClientAccount>;

export type SinglePaymentRows = {
  label: vp.SinglePaymentLabel;
  id: vp.SinglePaymentId;
  require: (ctx: SinglePayment) => boolean;
  preset: keyof vp.TextFieldPresets | undefined;
  type: 'input' | 'select' | 'singleDate' | 'checkbox' | 'multiSelector' | 'toggle' | 'radio';
  placeholder: string;
  tooltipMessage?: string;
  allowNewOption?: boolean;
  renderOption?: (o: SelectOption, args: { focused: boolean }) => React.ReactNode;
  customOptions?: SelectOption[];
  renderDropdownHeader?: (onClick: (value: string) => void, permissions: vp.ApiClientPermissions) => React.ReactNode;
  visible: (ctx: SinglePayment) => boolean;
  customAppendNoResultErrorMessage?: CustomAppendNoResultErrorMessage;
  maxLength?: number;
  disableFiltering?: (ctx: SinglePayment) => boolean;
  searchCallback?: (term: string) => void;
  onChangeCallBack?: (
    event: ChangeEvent<HTMLInputElement>,
    ctx: SinglePayment,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
    setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void,
  ) => void;
  SelectLabel?: (ctx: SinglePayment) => string | undefined;
  getSelectOptions?: (ctx: SinglePayment) => { [key: string]: string };
  getErrorMessage?: (ctx: SinglePayment) => string | string[] | null;
  getGoogleCountries?: (ctx: SinglePayment) => { [key: string]: string };
  getMultiSelectOptions?: (ctx: SinglePayment) => ItemProps[];
  getRadioOptions?: (ctx: SinglePayment) => { [key: string]: string | JSX.Element };
  handleGoogleResults?: (ctx: SinglePayment, addressComponents: vp.AddressComponents) => void;
  disabled: (ctx: SinglePayment) => boolean;
  getSubElement?: (ctx: SinglePayment) => JSX.Element | undefined;
  onBlur?: () => void;
};

export type Ctx = SinglePayment;
export type TransactionDetail = SinglePaymentRows;
export type ContactDetail = SinglePaymentRows;
export type AddressDetail = SinglePaymentRows;
export type BankDetail = SinglePaymentRows;
export type InteracDetail = SinglePaymentRows;
export type RequestDetail = SinglePaymentRows;

export const buildSinglePaymentRows = (
  moreInfoNeeded: boolean,
  FinancialInstitutions: FinancialInstitutionsType,
  financialInstitutionBranches: FinancialInstitutionsType,
  PaymentMethod: string,
  BankingPaymentRail: string,
  currency: string,
  selectedclientAccountDetail: (ClientAccoun: vp.ClientAccount) => void,
  autoDepositEnabled: boolean,
  accountHolderName?: string,
): [TransactionDetail[], ContactDetail[], AddressDetail[], BankDetail[], InteracDetail[], RequestDetail[]] => {
  const {
    response: {
      TransactionLabelOverrideEnabled,
      ClientAccountsEnabled,
      USDEFTSendEnabled,
      Country,
      KYCLightEnabled,
      LoginUserName,
      PaypalWithdrawEnabled,
      VenmoWithdrawEnabled,
      ACHWithdrawProcessor,
    },
  } = useClientData();
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const [InteracEmailsOptions, setInteracEmailsOptions] = useState({});
  const [clientAccountOptions, setClientAccountOptions] = useState<SelectOption[]>([]);
  const [, , , fetchEmails] = useFetchInteracEmail({});
  const [, , , fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const [BankAccountsOptions, setBankAccountOptions] = useState({});
  const [bankInfo, setBankInfo] = useState<vp.ClientBankAccountsLists>();
  const [shouldInitializeBankList, setShouldInitializeBankList] = useState(true);
  const [shouldInitializeEmailList, setShouldInitializeEmailList] = useState(true);
  const [shouldInitializeClientList, setShouldInitializeClientList] = useState(true);

  const { fetchVenmoRecipients } = useVenmo();
  const { fetchPayPalRecipients } = usePayPal();
  const [payPalOptions, setPayPalOptions] = useState({});
  const [venmoOptions, setVenmoOptions] = useState({});
  const [shouldInitializePayPalList, setShouldInitializePayPalList] = useState(true);
  const [shouldInitializeVenmoList, setShouldInitializeVenmoList] = useState(true);
  const [originalPayPalList, setOriginalPayPalList] = useState({});
  const [originalVenmoList, setOriginalVenmoList] = useState({});
  const [contactVenmo, setContactVenmo] = useState<vp.VenmoRecipient[]>([]);
  const [contactPayPal, setContactPayPal] = useState<vp.PayPalRecipient[]>([]);

  const [, , , getClientAccountList] = useFetchClientAccountList({});
  const [clientAccountDetail, setClientAccountDetail] = useState<vp.ClientAccount>();
  const [originalBankList, setOriginalBankList] = useState({});
  const [originalEmailList, setOriginalEmailList] = useState({});
  const [originalClientList, setOriginalClientList] = useState<SelectOption[]>([]);
  const InteracCtx = useContext(InteracInfoContext);
  const SinglePaymentBankInfoCtx = useContext(SinglePaymentBankInfoContext);
  const { PayLinkEnabled } = useLockedFeatures();
  const hasRecipientName = InteracCtx.interacDetails?.RecipientName.trim() !== '';
  const hasEmailAddress = InteracCtx.interacDetails?.RecipientEmailAddress !== '';
  const hasPhoneNumber = InteracCtx.interacDetails?.RecipientPhoneNumber !== '';
  const hasQuestion = InteracCtx.interacDetails?.Question !== '';
  const hasAnswer = InteracCtx.interacDetails?.Answer !== '';
  const PAYPAL = 'PayPal';
  const VENMO = 'Venmo';

  const CurrencyOptions = {
    ...vp.CURRENCY_SELECT_OPTIONS,
  };

  const CADOnly: { [key: string]: string } = {
    CAD: 'CAD',
  };

  const FrequencyOptions = {
    ...vp.FREQUENCY_OPTIONS,
  };

  const InteracOptions = { ...vp.ETRANSFER_CONNECTION_OPTIONS };

  const PaymentOptions: { [key: string]: string } = {
    'Bank Account': 'Bank Account',
    'Interac e-Transfer': '<i>Interac</i> e-Transfer<sup>&reg;</sup>',
    PayPal: 'PayPal',
  };

  const BankingPaymentRailOptions: { [key: string]: string } = {
    eft: 'Electronic Funds Transfer',
    vopayinstant: 'VoPay Instant',
  };

  const USPaymentOptions: { [key: string]: string } = {
    'Bank Account': 'Bank Account',
    PayPal: 'PayPal',
    Venmo: 'Venmo',
  };

  const USBankingPaymentRailOptions: { [key: string]: string } = {
    ach: 'Automated Clearing House',
    rtp: 'Real-Time Payment',
    fedwire: 'Fedwire',
  };

  const ScheduleTypeOptions: { [key: string]: string } = {
    single: 'One Time',
    recurring: 'Recurring',
  };

  const EndingOptions: { [key: string]: string } = {
    'End Date': 'End Date',
    Payments: 'Payments',
  };

  const SemiMonthlyDateOptions = {
    SemiMonthFirstAndFifteenth: '1st and 15th Day of the Month',
    SemiMonthFifteenthAndLast: '15th and Last Day of the Month',
  };

  const AccountConnectionMethods: ItemProps[] = [
    {
      label: 'Online',
      value: 'Online',
      checked: true,
      isReadOnly: false,
      isDisabled: false,
      isLocked: false,
    },
    {
      label: 'Manual',
      value: 'Manual',
      checked: true,
      isReadOnly: false,
      isDisabled: false,
    },
  ];

  const noResultErrorMessage: CustomAppendNoResultErrorMessage = {
    label: 'is not in your contact list. Please select a current contact or add a new one.',
    style: { color: 'red' },
  };

  const getNotificationMethodOptions = (defaultMethod: vp.ElinxNotificationMethod) => {
    return {
      email: 'Email Address',
      sms: 'SMS',
      ...(defaultMethod === 'whatsapp' && { whatsapp: 'WhatsApp' }),
    };
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (clientAccountDetail) {
      selectedclientAccountDetail(clientAccountDetail);
    }
  }, [clientAccountDetail]);

  async function init() {
    if (ClientAccountsEnabled === '1') {
      handleClientAccountSearch('');
      setShouldInitializeClientList(true);
    } else {
      handleBankAccountsSearch(accountHolderName || '');
      handleEmailSearch(accountHolderName || '');
      if (PaypalWithdrawEnabled) {
        handlePayPalSearch(accountHolderName || '');
      }
      if (VenmoWithdrawEnabled) {
        handleVenmoSearch(accountHolderName || '');
      }
      setShouldInitializeBankList(true);
      setShouldInitializeEmailList(true);
    }
  }

  const [contactClientAccounts, setContactClientAccounts] = useState({});
  const [contactBankAccounts, setContactBankAccounts] = useState({});
  const [contactEmail, setContactEmail] = useState<{
    [key: string]: vp.InteracEmailList;
  }>({});
  const { userSettings } = useUserSettingsProvider();

  async function handleClientAccountSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    const res: vp.ClientAccountListResponse = await getClientAccountList({
      ClientType: '',
      Limit: 100,
      SearchText: query === '' ? undefined : query,
    });
    setDataFetchingInfo(false);

    if (res.Success) {
      setContactClientAccounts(res.ClientAccounts);
    } else {
      setContactClientAccounts({});
    }
  }

  async function handleBankAccountsSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    const res: vp.ClientBankAccountsResponse = await fetchClientBankAccounts({
      PortalUser: LoginUserName,
      ClientControlled: false,
      SearchText: query,
      Token: '',
      Limit: 100,
      Timezone: userSettings.timezone,
    });
    setDataFetchingInfo(false);

    if (res.Success) {
      setContactBankAccounts(res.BankAccounts);
    } else {
      setContactBankAccounts({});
    }
  }

  async function handleEmailSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');

    const res: vp.InteracEmailResponse = await fetchEmails({
      ClientControlled: false,
      SearchText: query,
      Limit: 100,
    });
    setDataFetchingInfo(false);

    if (res.Success) {
      setContactEmail(res.InteracEmails);
    } else {
      setContactEmail({});
    }
  }
  async function handleVenmoSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    const res = await fetchVenmoRecipients({
      SearchText: query === '' ? undefined : query,
      Limit: 100,
      Offset: 0,
    });
    setDataFetchingInfo(false);

    // adapt to the shape returned by your API
    if (res && res.Success) {
      setContactVenmo(res.VenmoRecipients || []);
    } else if (Array.isArray(res)) {
      // fallback if the hook returns an array directly
      setContactVenmo(res);
    } else {
      setContactVenmo([]);
    }
  }

  async function handlePayPalSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    const res = await fetchPayPalRecipients({
      SearchText: query === '' ? undefined : query,
      Offset: 0,
      Limit: 100,
    });
    setDataFetchingInfo(false);

    // adapt to the shape returned by your API
    if (res && res.Success) {
      setContactPayPal(res.PaypalRecipients || []);
    } else if (Array.isArray(res)) {
      // fallback if the hook returns an array directly
      setContactPayPal(res);
    } else {
      setContactPayPal([]);
    }
  }
  useEffect(() => {
    setPayPalOptions(buildPayPalOptions(contactPayPal || []));
  }, [contactPayPal]);
  useEffect(() => {
    setVenmoOptions(buildVenmoOptions(contactVenmo || []));
  }, [contactVenmo]);
  useEffect(() => {
    if (shouldInitializePayPalList && Object.keys(payPalOptions).length > 0) {
      setOriginalPayPalList(payPalOptions);
      setShouldInitializePayPalList(false);
    }
  }, [payPalOptions]);

  useEffect(() => {
    if (shouldInitializeVenmoList && Object.keys(venmoOptions).length > 0) {
      setOriginalVenmoList(venmoOptions);
      setShouldInitializeVenmoList(false);
    }
  }, [venmoOptions]);
  useEffect(() => {
    if (shouldInitializeBankList && Object.keys(BankAccountsOptions).length > 0) {
      setOriginalBankList(BankAccountsOptions);
      setShouldInitializeBankList(false);
    }
  }, [BankAccountsOptions]);

  useEffect(() => {
    if (shouldInitializeEmailList && Object.keys(InteracEmailsOptions).length > 0) {
      setOriginalEmailList(InteracEmailsOptions);
      setShouldInitializeEmailList(false);
    }
  }, [InteracEmailsOptions]);

  useEffect(() => {
    if (shouldInitializeClientList && Object.keys(clientAccountOptions).length > 0) {
      setOriginalClientList(clientAccountOptions);
      setShouldInitializeClientList(false);
    }
  }, [clientAccountOptions]);

  useEffect(() => {
    setInteracEmailsOptions(buildInteracEmailsOptions(Object.values(contactEmail)));
  }, [contactEmail]);
  useEffect(() => {
    setClientAccountOptions(buildClientAccountOptions(Object.values(contactClientAccounts)));
  }, [contactClientAccounts]);

  useEffect(() => {
    setBankAccountOptions(
      buildBankAccountOptions(
        (Object.values(contactBankAccounts || {}) as vp.ClientBankAccountsLists[]).filter(
          (data) => data.Currency === currency,
        ),
      ),
    );
  }, [contactBankAccounts, currency]);
  function buildVenmoOptions(lists: vp.VenmoRecipient[]) {
    return lists.reduce((accumulator, data) => {
      return { ...accumulator, [data.VenmoToken]: `${data.RecipientName} ${data.RecipientID}` };
    }, {});
  }
  function buildPayPalOptions(lists: vp.PayPalRecipient[]) {
    return lists.reduce((accumulator, data) => {
      return { ...accumulator, [data.PayPalToken]: `${data.RecipientName} ${data.RecipientID}` };
    }, {});
  }

  function buildBankAccountOptions(lists: vp.ClientBankAccountsLists[]) {
    return lists.reduce(
      (accumulator, data) => ({
        ...accumulator,
        [data.Token]: `${data.Currency} ${data.AccountHolderName} ${getMaskedNumber(data.AccountNumber)} `,
      }),
      {},
    );
  }

  function getMaskedNumber(num: string) {
    const lastFourDigits = num ? num.slice(-4) : '';
    return `***${lastFourDigits}`;
  }

  const transactionDetails: TransactionDetail[] = [
    {
      label: vp.SinglePaymentLabel.PaymentMethod,
      id: vp.SinglePaymentId.PaymentMethod,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: () => true,
      getSelectOptions: () => (Country === 'CA' ? PaymentOptions : USPaymentOptions),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.CancellationPeriod,
      id: vp.SinglePaymentId.CancellationPeriod,
      require: () => false,
      preset: 'number',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Interac e-Transfer',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.BankingPaymentRail,
      id: vp.SinglePaymentId.BankingPaymentRail,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Bank Account',
      getSelectOptions: () => (Country === 'CA' ? BankingPaymentRailOptions : USBankingPaymentRailOptions),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Currency,
      id: vp.SinglePaymentId.Currency,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: () => true,
      getSelectOptions: () =>
        Country === 'US' ? vp.US_CURRENCY_ONLY : USDEFTSendEnabled === '0' ? CADOnly : CurrencyOptions,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Contact,
      id: vp.SinglePaymentId.Contact,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: () => ClientAccountsEnabled === '0',
      customAppendNoResultErrorMessage: noResultErrorMessage,
      onBlur: () => {
        setBankAccountOptions(originalBankList);
      },
      SelectLabel: (ctx: SinglePayment) => {
        if (ctx.Contact === 'provide') return 'New, Provide';
        if (ctx.Contact === 'request') return 'New, Request';

        if (
          ctx.PaymentMethod === 'Interac e-Transfer' &&
          InteracCtx.interacDetails &&
          ctx.Contact &&
          ctx.Contact !== 'provide' &&
          ctx.Contact !== ''
        ) {
          return `${InteracCtx.interacDetails?.RecipientName || ''} (${
            InteracCtx.interacDetails?.RecipientEmailAddress || InteracCtx.interacDetails?.RecipientPhoneNumber || ''
          })`;
        }

        if (ctx.PaymentMethod === 'Bank Account' && ctx.Contact && ctx.Contact !== 'provide' && ctx.Contact !== '') {
          return `${bankInfo?.Currency || ''} ${bankInfo?.AccountHolderName || ''} ${getMaskedNumber(
            bankInfo?.AccountNumber || '',
          )}`;
        }
        return undefined;
      },
      onChangeCallBack: (event, ctx) => {
        const Contact = event.target.value;

        if (
          Contact === 'provide' ||
          (Contact !== '' && ctx.PaymentMethod === 'Interac e-Transfer' && !vp.toBool(ClientAccountsEnabled))
        ) {
          if (
            !vp.toBool(ClientAccountsEnabled) &&
            Contact !== 'provide' &&
            Contact !== '' &&
            ctx.PaymentMethod === 'Interac e-Transfer'
          ) {
            // move or use other list or all items
            const interacEmail = Object.values(contactEmail || {}).find((interac) => interac.Token === Contact);

            if (interacEmail) {
              InteracCtx.updateInterac(false, true, {
                RecipientPhoneNumber: interacEmail?.RecipientPhoneNumber || '',
                RecipientName: interacEmail?.RecipientName || '',
                RecipientEmailAddress: interacEmail?.RecipientEmailAddress || '',
                Question: interacEmail?.Question || '',
                Answer: interacEmail?.Answer || '',
                ClientReferenceNumber: interacEmail?.ClientReferenceNumber,
              });
            }
          } else if (
            !vp.toBool(ClientAccountsEnabled) &&
            Contact === 'provide' &&
            ctx.PaymentMethod === 'Interac e-Transfer'
          ) {
            if (
              InteracCtx.interacDetails?.RecipientPhoneNumber !== '' ||
              InteracCtx.interacDetails?.RecipientName !== '' ||
              InteracCtx.interacDetails?.RecipientEmailAddress !== '' ||
              InteracCtx.interacDetails?.Question !== '' ||
              InteracCtx.interacDetails?.Answer !== '' ||
              InteracCtx.interacDetails?.ClientReferenceNumber !== ''
            ) {
              InteracCtx.updateInterac(false, true, {
                RecipientPhoneNumber: '',
                RecipientName: '',
                RecipientEmailAddress: '',
                Question: '',
                Answer: '',
                ClientReferenceNumber: '',
              });
            }
          }
        }
        if (
          Contact === 'provide' ||
          (Contact !== '' && ctx.PaymentMethod === 'Bank Account' && !vp.toBool(ClientAccountsEnabled))
        ) {
          const contactBankAccount = Object.values(contactBankAccounts || {}).find(
            (bank) => (bank as vp.ClientBankAccountsLists).Token === Contact,
          ) as vp.ClientBankAccountsLists | undefined;

          if (contactBankAccount) {
            setBankInfo(contactBankAccount || undefined);
            setBankAccountOptions(originalBankList);

            SinglePaymentBankInfoCtx.updateBankAccount({
              ...contactBankAccount,
            });
          }
        }
      },
      disableFiltering: (ctx: SinglePayment) =>
        ctx.PaymentMethod === 'Interac e-Transfer' ||
        ctx.PaymentMethod === 'Bank Account' ||
        ctx.PaymentMethod === PAYPAL ||
        ctx.PaymentMethod === VENMO,
      searchCallback: (value: string) => {
        if (PaymentMethod === 'Bank Account') {
          if (value === '') {
            setBankAccountOptions(originalBankList);
          } else {
            handleBankAccountsSearch(value);
          }
        }
        if (PaymentMethod === 'Interac e-Transfer') {
          if (value === '') {
            setInteracEmailsOptions(originalEmailList);
          } else {
            handleEmailSearch(value);
          }
        }
        if (PaymentMethod === PAYPAL) {
          if (value === '') {
            setPayPalOptions(originalPayPalList);
          } else {
            handlePayPalSearch(value);
          }
        }
        if (PaymentMethod === VENMO) {
          if (value === '') {
            setVenmoOptions(originalVenmoList);
          } else {
            handleVenmoSearch(value);
          }
        }
      },
      getSelectOptions: (ctx: SinglePayment) =>
        ctx.PaymentMethod === 'Bank Account'
          ? BankAccountsOptions
          : ctx.PaymentMethod === PAYPAL
            ? payPalOptions
            : ctx.PaymentMethod === VENMO
              ? venmoOptions
              : InteracEmailsOptions,
      renderDropdownHeader: (onClick) => (
        <>
          {/* TODO: Enable the option to add new contact payment information for Fedwire and RTP */}
          {BankingPaymentRail !== 'rtp' && BankingPaymentRail !== 'Fedwire' && (
            <div className={styles.dropdownHeader}>
              New
              <div>
                {PaymentMethod !== PAYPAL && PaymentMethod !== VENMO && (
                  <button type="button" onClick={() => onClick('provide')}>
                    + Provide new contact payment information
                  </button>
                )}

                {PayLinkEnabled && (
                  <button type="button" onClick={() => onClick('request')}>
                    + Request new contact payment information
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      ),
      disabled: () => false,
    },

    {
      label: vp.SinglePaymentLabel.ClientAccount,
      id: vp.SinglePaymentId.ClientAccount,
      require: () => ClientAccountsEnabled === '1',
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: () => ClientAccountsEnabled === '1',
      customOptions: clientAccountOptions,
      renderOption: (o, args) => (
        <OptionLabel clientName={o.ClientName} currency={currency} balanceAccount={getBalanceAccount(o)} {...args} />
      ),
      disabled: () => false,
      disableFiltering: () => true,
      onChangeCallBack: (event, _ctx, setFieldValue, setFieldTouched) => {
        const ClientAccountId = event.target.value;
        const ClientAccount = Object.values(clientAccountOptions || {}).find(
          (account) => account.ClientAccountID === ClientAccountId,
        );

        if (ClientAccount) {
          setFieldTouched('IdentificationNumber', false);
          setFieldValue('IdentificationNumber', ClientAccount?.IdentificationNumber);
          setFieldValue('RecipientPhoneNumber', ClientAccount?.PhoneNumber || '');
          setFieldValue('ClientAccountPhoneNumber', ClientAccount?.PhoneNumber || '');
          setClientAccountDetail(ClientAccount);
        }
      },
      SelectLabel: () => {
        return clientAccountDetail?.ClientName;
      },
      searchCallback: (value: string) => {
        if (value === '') {
          setClientAccountOptions(originalClientList);
        } else {
          handleClientAccountSearch(value);
        }
      },
      getSubElement: (ctx: SinglePayment) => {
        const clientAccountBalance = Object.values(clientAccountDetail?.Balances || {}).find(
          (balance) => balance.Currency === ctx.Currency,
        );

        return ctx.ClientAccount !== '' && clientAccountDetail ? (
          <div className={styles.AvailableBalance}>
            {clientAccountBalance && (
              <span>
                Available Balance {ctx.Currency}{' '}
                {vp.formattedCurrency((clientAccountBalance.Balance - clientAccountBalance.PendingBalance).toString())}
              </span>
            )}
            {!clientAccountBalance && (
              <span>
                Available Balance {ctx.Currency} {vp.formattedCurrency('0')}
              </span>
            )}
          </div>
        ) : undefined;
      },
    },
    {
      label: vp.SinglePaymentLabel.IdentificationNumber,
      id: vp.SinglePaymentId.IdentificationNumber,
      require: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.BankingPaymentRail === 'ach' &&
        !vp.toBool(KYCLightEnabled) &&
        (ctx.Contact !== 'new' || vp.toBool(ClientAccountsEnabled)) &&
        ACHWithdrawProcessor === 'fiserv',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.BankingPaymentRail === 'ach' &&
        !vp.toBool(KYCLightEnabled) &&
        (ctx.Contact !== 'new' || vp.toBool(ClientAccountsEnabled)) &&
        ACHWithdrawProcessor === 'fiserv',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Amount,
      id: vp.SinglePaymentId.Amount,
      require: () => true,
      preset: 'currency',
      type: 'input',
      placeholder: '0.00',
      visible: () => true,
      disabled: () => false,
    },

    {
      label: vp.SinglePaymentLabel.ContactType,
      id: vp.SinglePaymentId.ContactType,
      require: (ctx: SinglePayment) =>
        (ctx.PaymentMethod !== 'Interac e-Transfer' && isContactProvide(ctx)) || isContactRequest(ctx),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SinglePayment) =>
        (ctx.PaymentMethod !== 'Interac e-Transfer' && isContactProvide(ctx)) || isContactRequest(ctx),
      getSelectOptions: () => vp.ACCOUNT_TYPE_OPTIONS,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.ScheduleType,
      id: vp.SinglePaymentId.ScheduleType,
      require: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      getSelectOptions: () => ScheduleTypeOptions,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.TakeFromAccountOn,
      id: vp.SinglePaymentId.TakeFromAccountOn,
      require: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'single' &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'single' &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.StartDate,
      id: vp.SinglePaymentId.StartDate,
      require: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Frequency,
      id: vp.SinglePaymentId.Frequency,
      require: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      getSelectOptions: () => FrequencyOptions,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.SemiMonthlyDates,
      id: vp.SinglePaymentId.SemiMonthlyDates,
      require: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        ctx.Frequency === 'semi-monthly',
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        ctx.Frequency === 'semi-monthly',
      getSelectOptions: () => SemiMonthlyDateOptions,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Ending,
      id: vp.SinglePaymentId.Ending,
      require: () => false,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      getSelectOptions: () => EndingOptions,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.EndDate,
      id: vp.SinglePaymentId.EndDate,
      require: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        ctx.Ending === 'End Date',
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        ctx.ScheduleType === 'recurring' &&
        ctx.Ending === 'End Date',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.EndingAfterPayments,
      id: vp.SinglePaymentId.EndingAfterPayments,
      require: (ctx: SinglePayment) => ctx.Ending === 'Payments' && Country === 'CA' && ctx.Currency === 'CAD',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.Ending === 'Payments' && Country === 'CA' && ctx.Currency === 'CAD',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Description,
      id: vp.SinglePaymentId.Description,
      require: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        (ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' &&
            !vp.IsTodayDate(moment(Number(ctx.TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      preset: 'text',
      type: 'input',
      placeholder: 'Schedule Description',
      visible: (ctx: SinglePayment) =>
        ctx.PaymentMethod == 'Bank Account' &&
        (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
        (ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' &&
            !vp.IsTodayDate(moment(Number(ctx.TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
        Country === 'CA' &&
        ctx.Currency === 'CAD',
      disabled: () => false,
    },
    {
      // figure why it is not
      label: vp.SinglePaymentLabel.Message,
      id: vp.SinglePaymentId.Message,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Interac e-Transfer',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.RecipientName,
      id: vp.SinglePaymentId.RecipientName,
      require: (ctx: SinglePayment) => ctx.BankingPaymentRail === 'rtp',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.BankingPaymentRail === 'rtp',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.SecCode,
      id: vp.SinglePaymentId.SecCode,
      require: () => false,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: (ctx: SinglePayment) =>
        moreInfoNeeded && ctx.PaymentMethod === 'Bank Account' && ctx.BankingPaymentRail === 'ach',
      getSelectOptions: () => ({
        PPD: 'PPD - Prearranged Payment and Deposit',
        CCD: 'CCD - Corporate Credit or Debit',
        WEB: 'WEB - Internet-Initiated Entry',
        TEL: 'TEL - Telephone-Initiated Entry',
        ARC: 'ARC - Accounts Receivable Entry',
        IAT: 'IAT - International ACH Transaction',
        POP: 'POP - Point-of-Purchase Entry',
      }),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.ClientReferenceNumber,
      id: vp.SinglePaymentId.ClientReferenceNumber,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      maxLength: 30,
      visible: () => moreInfoNeeded,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.TransactionLabel,
      id: vp.SinglePaymentId.TransactionLabel,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      maxLength: 30,
      visible: (ctx: SinglePayment) =>
        TransactionLabelOverrideEnabled === '1' && ctx.PaymentMethod === 'Bank Account' && moreInfoNeeded,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Notes,
      id: vp.SinglePaymentId.Notes,
      require: (ctx: SinglePayment) => ctx.ScheduleType === 'recurring',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => moreInfoNeeded || ctx.ScheduleType === 'recurring',
      disabled: () => false,
    },
  ];

  const contactDetails: ContactDetail[] = [
    {
      label: vp.SinglePaymentLabel.FirstName,
      id: vp.SinglePaymentId.FirstName,
      require: (ctx: SinglePayment) => (isContactProvide(ctx) || isRequestNew(ctx)) && ctx.ContactType === 'Individual',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => (isContactProvide(ctx) || isRequestNew(ctx)) && ctx.ContactType === 'Individual',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.LastName,
      id: vp.SinglePaymentId.LastName,
      require: (ctx: SinglePayment) => (isContactProvide(ctx) || isRequestNew(ctx)) && ctx.ContactType === 'Individual',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => (isContactProvide(ctx) || isRequestNew(ctx)) && ctx.ContactType === 'Individual',
      disabled: () => false,
    },

    {
      label: vp.SinglePaymentLabel.CompanyName,
      id: vp.SinglePaymentId.CompanyName,
      require: (ctx: SinglePayment) =>
        (isContactProvide(ctx) || isContactRequest(ctx)) && ctx.ContactType === 'Business',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) =>
        (isContactProvide(ctx) || isContactRequest(ctx)) && ctx.ContactType === 'Business',
      disabled: () => false,
    },

    {
      label: vp.SinglePaymentLabel.RecipientPhoneNumber,
      id: vp.SinglePaymentId.RecipientPhoneNumber,
      require: (ctx: SinglePayment) =>
        !isRequestNew(ctx) && ctx.Country === 'US' && ctx.PaymentMethod === 'Bank Account',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.Country === 'US' && ctx.PaymentMethod === 'Bank Account',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.RecipientEmailAddress,
      id: vp.SinglePaymentId.RecipientEmailAddress,
      require: (ctx: SinglePayment) =>
        (ctx.Country === 'US' && ctx.PaymentMethod === 'Bank Account' && !isRequestNew(ctx)) ||
        (isRequestNew(ctx) && ctx.NotificationMethod === 'email'),
      preset: 'email',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) =>
        (ctx.Country === 'US' && ctx.PaymentMethod === 'Bank Account' && !isRequestNew(ctx)) || isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.RecipientPhoneNumber,
      id: vp.SinglePaymentId.ReceiverPhoneNumber,
      require: (ctx: SinglePayment) => isRequestNew(ctx) && ctx.NotificationMethod === 'sms',
      preset: 'email',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Nickname,
      id: vp.SinglePaymentId.Nickname,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Interac e-Transfer',
      disabled: () => false,
    },
  ];

  const addressDetail: AddressDetail[] = [
    {
      label: vp.SinglePaymentLabel.Address1,
      id: vp.SinglePaymentId.Address1,
      require: (ctx: SinglePayment) =>
        isContactProvide(ctx) || (Country === 'US' && ctx.PaymentMethod === 'Bank Account'),
      preset: 'text',
      type: 'input',
      getGoogleCountries: (ctx: SinglePayment) => ({ [ctx.Country || 'CA']: ctx.Country || 'CA' }),
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Bank Account',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Address2,
      id: vp.SinglePaymentId.Address2,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Bank Account',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.City,
      id: vp.SinglePaymentId.City,
      require: (ctx: SinglePayment) =>
        isContactProvide(ctx) || (Country === 'US' && ctx.PaymentMethod === 'Bank Account'),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Bank Account',
      disabled: () => false,
    },
    {
      label: Country === 'CA' ? vp.SinglePaymentLabel.Province : vp.SinglePaymentLabel.State,
      id: vp.SinglePaymentId.Province,
      require: (ctx: SinglePayment) =>
        isContactProvide(ctx) || (Country === 'US' && ctx.PaymentMethod === 'Bank Account'),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Bank Account',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.Country,
      id: vp.SinglePaymentId.Country,
      require: (ctx: SinglePayment) =>
        isContactProvide(ctx) || (Country === 'US' && ctx.PaymentMethod === 'Bank Account'),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Bank Account',
      getSelectOptions: () => vp.CANADA_US_COUNTRY_OPTION,
      disabled: () => false,
    },
    {
      label: Country === 'CA' ? vp.SinglePaymentLabel.PostalCode : vp.SinglePaymentLabel.ZipCode,
      id: vp.SinglePaymentId.PostalCode,
      require: (ctx: SinglePayment) =>
        isContactProvide(ctx) || (Country === 'US' && ctx.PaymentMethod === 'Bank Account'),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.PaymentMethod === 'Bank Account',
      disabled: () => false,
    },
  ];

  const bankDetail: BankDetail[] = [
    {
      label: vp.SinglePaymentLabel.FinancialInstitutionNumber,
      id: vp.SinglePaymentId.FinancialInstitutionNumber,
      require: (ctx: SinglePayment) => isContactProvide(ctx),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => Country === 'CA',
      getSelectOptions: () => FinancialInstitutions,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.BranchTransitNumber,
      id: vp.SinglePaymentId.BranchTransitNumber,

      require: (ctx: SinglePayment) => isContactProvide(ctx),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => Country === 'CA',
      getSelectOptions: () => financialInstitutionBranches,
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.ABARoutingNumber,
      id: vp.SinglePaymentId.ABARoutingNumber,
      require: (ctx: SinglePayment) => isContactProvide(ctx),
      preset: 'number',
      type: 'input',
      placeholder: '',
      visible: () => Country === 'US',
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.AccountNumber,
      id: vp.SinglePaymentId.AccountNumber,
      require: (ctx: SinglePayment) => isContactProvide(ctx) && ctx.PaymentMethod === 'Bank Account',
      preset: 'number',
      type: 'input',
      placeholder: '',
      visible: () => true,
      disabled: () => false,
    },
  ];

  const interacDetails: InteracDetail[] = [
    {
      label: vp.SinglePaymentLabel.EtransferConnectionType,
      id: vp.SinglePaymentId.EtransferConnectionType,
      require: (ctx: SinglePayment) => {
        if (
          ctx.PaymentMethod === 'Interac e-Transfer' &&
          !vp.toBool(ClientAccountsEnabled) &&
          InteracCtx.addInteracEmail
        ) {
          return true;
        }

        if (isContactProvide(ctx) && ctx.PaymentMethod === 'Interac e-Transfer') {
          return true;
        }

        return false;
      },
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => true,
      getSelectOptions: () => InteracOptions,
      disabled: (ctx: SinglePayment) => {
        if (
          ctx.PaymentMethod === 'Interac e-Transfer' &&
          vp.toBool(ClientAccountsEnabled) &&
          !InteracCtx.addInteracEmail
        ) {
          return true;
        }
        return false;
      },
    },
    {
      label: vp.SinglePaymentLabel.RecipientName,
      id: vp.SinglePaymentId.RecipientName,
      require: (ctx: SinglePayment) => ctx.PaymentMethod === 'Interac e-Transfer',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
      disabled: () => {
        if (hasRecipientName && vp.toBool(ClientAccountsEnabled)) {
          return true;
        }
        return false;
      },
    },
    {
      label: vp.SinglePaymentLabel.RecipientPhoneNumber,
      id: vp.SinglePaymentId.RecipientPhoneNumber,
      require: (ctx: SinglePayment) => {
        if (ctx.PaymentMethod === 'Interac e-Transfer' && ctx.EtransferConnectionType === 'phone') {
          return true;
        }
        return false;
      },
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.EtransferConnectionType === 'phone',
      disabled: (ctx: SinglePayment) => {
        if (ctx.PaymentMethod === 'Interac e-Transfer' && ctx.EtransferConnectionType === 'phone') {
          if (hasPhoneNumber && vp.toBool(ClientAccountsEnabled)) {
            return true;
          }
        }
        return false;
      },
    },
    {
      label: vp.SinglePaymentLabel.RecipientEmailAddress,
      id: vp.SinglePaymentId.RecipientEmailAddress,
      require: (ctx: SinglePayment) =>
        ctx.PaymentMethod === 'Interac e-Transfer' && ctx.EtransferConnectionType === 'email',
      preset: 'email',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => ctx.EtransferConnectionType === 'email',
      disabled: (ctx: SinglePayment) => {
        if (ctx.PaymentMethod === 'Interac e-Transfer' && ctx.EtransferConnectionType === 'email') {
          if (hasEmailAddress && vp.toBool(ClientAccountsEnabled)) {
            return true;
          }
        }
        return false;
      },
    },
    {
      label: vp.SinglePaymentLabel.Question,
      id: vp.SinglePaymentId.Question,
      require: (ctx: SinglePayment) => ctx.PaymentMethod === 'Interac e-Transfer',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => !autoDepositEnabled,
      disabled: () => {
        if (hasQuestion && vp.toBool(ClientAccountsEnabled)) {
          return true;
        }

        return false;
      },
    },
    {
      label: vp.SinglePaymentLabel.Answer,
      id: vp.SinglePaymentId.Answer,
      require: (ctx: SinglePayment) => ctx.PaymentMethod === 'Interac e-Transfer',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => !autoDepositEnabled,
      disabled: () => {
        if (hasAnswer && vp.toBool(ClientAccountsEnabled)) {
          return true;
        }

        return false;
      },
    },
  ];

  const requestDetails: RequestDetail[] = [
    {
      label: vp.SinglePaymentLabel.SenderName,
      id: vp.SinglePaymentId.SenderName,
      require: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.AccountConnectionMethod,
      id: vp.SinglePaymentId.AccountConnectionMethod,
      require: () => false,
      preset: 'text',
      type: 'multiSelector',
      placeholder: '',
      visible: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      getMultiSelectOptions: (ctx: SinglePayment) => {
        let connectionMethods: ItemProps[] = [];
        if (
          (ctx.BankingPaymentRail === 'eft' || ctx.BankingPaymentRail === 'ach') &&
          (isPaymentInformationRequest(ctx) || isContactRequest(ctx))
        ) {
          let methods: string | string[] = [];
          if (ctx.AccountConnectionMethod) {
            methods = ctx.AccountConnectionMethod.split('|');
          }

          connectionMethods = AccountConnectionMethods.map((item) => ({
            ...item,
            checked: methods.includes(item.value),
          }));
        }
        return connectionMethods || [];
      },
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.MessageForRecipient,
      id: vp.SinglePaymentId.MessageForRecipient,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      tooltipMessage:
        'The payment/collection transaction will be initiated immediately after the user has connected their bank account.',
      visible: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.InvoiceNumber,
      id: vp.SinglePaymentId.InvoiceNumber,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.RecipientNotifications,
      id: vp.SinglePaymentId.RecipientNotifications,
      require: () => false,
      preset: 'text',
      type: 'toggle',
      placeholder: '',
      tooltipMessage: 'Send eLinx notification to the Recipient',
      visible: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.RecipientReminders,
      id: vp.SinglePaymentId.RecipientReminders,
      require: () => false,
      preset: 'text',
      type: 'toggle',
      placeholder: '',
      tooltipMessage: 'Send eLinx reminder to the Recipient i.e. Successful, Accepted, and Rejected.',
      visible: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      disabled: () => false,
    },
    {
      label: vp.SinglePaymentLabel.ReminderFreqeuncy,
      id: vp.SinglePaymentId.ReminderFreqeuncy,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      disabled: (ctx: SinglePayment) => !ctx.SendReminder,
    },
    {
      label: vp.SinglePaymentLabel.NotificationMethod,
      id: vp.SinglePaymentId.NotificationMethod,
      require: () => false,
      preset: 'text',
      type: 'radio',
      placeholder: '',
      getRadioOptions: (ctx: SinglePayment) => getNotificationMethodOptions(ctx.NotificationMethod || 'email'),
      visible: (ctx: SinglePayment) => isPaymentInformationRequest(ctx) || isContactRequest(ctx),
      disabled: (ctx: SinglePayment) => !ctx.SendNotification,
    },
  ];

  return [transactionDetails, contactDetails, addressDetail, bankDetail, interacDetails, requestDetails];

  function buildInteracEmailsOptions(lists: vp.InteracEmailList[]) {
    const options = lists.reduce(
      (accumulator, data) => ({
        ...accumulator,
        [data.Token]: `${data.RecipientName}  (${data.RecipientEmailAddress || data.RecipientPhoneNumber || ''})`,
      }),
      {},
    );
    return { ...options };
  }

  function buildClientAccountOptions(lists: vp.ClientAccount[]) {
    const options: SelectOption[] = [];
    /*
     * forEach was used instead of reduce because when the list got large around 30000 client
     * accounts the overhead of maintaining the accumulator caused significant performance issues
     * when compared to forEach
     */
    lists.forEach((data) => {
      if (data.IsActive) {
        options.push({
          ...data,
          label: data.ClientName,
          value: data.ClientAccountID,
          style: undefined,
        });
      }
    });

    return options;
  }

  function getBalanceAccount(data: vp.ClientAccount) {
    const balanceAccountObject = Object.values(data.Balances || {}).find((balance) => balance.Currency === currency);
    return (balanceAccountObject?.Balance || 0) - (balanceAccountObject?.PendingBalance || 0);
  }

  function isPaymentInformationRequest(ctx: Ctx) {
    return ctx.ProvideOrRequestPaymentInformation === 'request';
  }

  function isContactRequest(ctx: Ctx) {
    return ctx.Contact === 'request';
  }

  function isContactProvide(ctx: Ctx) {
    return ctx.Contact === 'provide';
  }

  function isRequestNew(ctx: Ctx) {
    return ctx.Contact === 'request';
  }
};

function OptionLabel({
  clientName,
  currency,
  balanceAccount,
  focused,
}: {
  clientName: string;
  currency: string;
  balanceAccount: number;
  focused: boolean;
}) {
  return (
    <div className={[styles.optionLabel, focused ? styles.focused : ''].join(' ')}>
      <div className={styles.optionLabelContent}>
        <div>{clientName}</div>
        <div className={styles.formatAvailableBalance}>{`(Available Bal. ${currency} $${vp.formattedCurrency(
          balanceAccount.toString(),
        )})`}</div>
      </div>
    </div>
  );
}
